import {
  Component,
  HostListener,
  Input,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { Toast, ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { fileToBase64 } from 'src/app/core/utils/data-processor.util';
import { DragUpload, DragUploadValue } from '../../models/components.models';
import { UntypedFormGroup } from '@angular/forms';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-drag-upload',
  templateUrl: './drag-upload.component.html',
  styleUrls: ['./drag-upload.component.scss'],
})
export class DragUploadComponent implements OnInit {
  @Input('form') form!: UntypedFormGroup;
  @Input('controlName') controlName: string = '';
  @Input('label') label!: string;
  @Input('requiredMessage') requiredMessage: string = 'This field is required';
  @Input('invalidMessage') invalidMessage: string = 'This field is invalid';
  @Input('allowedFileSize') allowedFileSize: number = 20;
  @Input('title') title: string = 'Documents';
  @Input('subTitle')
  subTitle: string = `Max allowed size is ${this.allowedFileSize} MB`;
  @Input('maxFiles') maxFiles: number = 1;
  @Input('mime') mime: string = '';
  @Input('clear') clear = new Subject();
  @Output('value') value = new EventEmitter<any>();
  required = false;
  subsink: SubSink = new SubSink();
  
  public dragObject: DragUpload = {
    valid: true,
    invalid: false,
    value: [],
  };
  public files: Array<Blob> = [];

  constructor(private toast: ToastrService) {}

  ngOnInit(): void {
    this.value.emit(this.dragObject);
    this.subsink.add(
    this.clear.subscribe(() => {
      this.dragObject = {
        valid: true,
        invalid: false,
        value: [],
      };
      this.files = [];
      this.value.emit(this.dragObject);
    }));
  }

  /**
   * Remove file
   * @param index index of file
   */
  removeFile = (row: number) => {
    this.files = this.files.filter((value, index) => index !== row);
    this.setObject();
  };

  /**
   * Set the dragObject
   */
  public setObject = () => {
    this.dragObject.valid = true;
    this.dragObject.invalid = false;
    for (let file of this.files)
      if (file.size > this.allowedFileSize * 1e6) {
        this.dragObject.valid = false;
        this.dragObject.invalid = true;
        break;
      }
    this.copyFilesToObject();
  };

  /**
   * Copy files to drop object in value key
   */
  public copyFilesToObject = async () => {
    this.dragObject.value = [];
    for (let file of this.files) {
      const b64 = <DragUploadValue>await fileToBase64(file);
      this.dragObject.value.push(b64);
    }
    this.value.emit(this.dragObject);
  };

  /**
   * @return true if file dropped is valid (mime)
   */
  areFilesValid = (files: Array<Blob>) => {
    const regex = new RegExp(this.mime, 'i');
    for (let file of files)
      if (!regex.test(file.type)) {
        this.toast.error('File selected is of invalid mime', 'Invalid file');
        return false;
      }
    return true;
  };

  /**
   * Load files
   */
  loadFiles = (event: any) => {
    if (
      this.files.length < this.maxFiles &&
      this.areFilesValid(event.target.files)
    ) {
      this.files.push(...event.target.files);
      this.setObject();
    }
    event.target.value = '';
  };

  /**
   * Get file name
   */
  getFileName = (file: any) => file['name'];

  /**
   * Set errors here priority wise
   * @return error message
   */
  getErrorMessage = () => {
    if (!this.control?.hasError('required')) return this.invalidMessage;
    return this.requiredMessage;
  };

  get control() {
    return this.form?.controls[this.controlName];
  }

  /**
   * Listeners
   */

  @HostListener('dragover', ['$event']) onDragOver(evt: any) {
    evt.preventDefault();
    evt.stopPropagation();
  }

  @HostListener('dragleave', ['$event']) public onDragLeave(evt: any) {
    evt.preventDefault();
    evt.stopPropagation();
  }

  //Drop listener
  @HostListener('drop', ['$event']) public ondrop(evt: any) {
    evt.preventDefault();
    evt.stopPropagation();
    this.loadFiles({
      target: {
        files: evt.dataTransfer.files,
      },
    });
  }

  ngOnDestroy(): void { 
    this.subsink.unsubscribe();
  }
}
